/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */


import apiClient from './client';
import { useAuthStore } from '@/store/authStore';
import type {
  DisputeListResponse,
  DisputeDetail,
  DisputeEvidence,
  DisputeTimeline,
  DisputeStats,
  SubmitEvidenceRequest,
} from '../types/disputes';

export const disputesApi = {
  getDisputes: async (params: {
    status?: string;
    reason?: string;
    companyOwnerId?: string;
    from?: string;
    to?: string;
    search?: string;
    sortBy?: string;
    sortDir?: string;
    page?: number;
    pageSize?: number;
  }): Promise<DisputeListResponse> => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });
    const response = await apiClient.get<DisputeListResponse>(
      `/Dispute?${searchParams.toString()}`
    );
    return response.data;
  },

  getDispute: async (id: string): Promise<DisputeDetail> => {
    const response = await apiClient.get<DisputeDetail>(`/Dispute/${id}`);
    return response.data;
  },

  getEvidence: async (disputeId: string): Promise<DisputeEvidence[]> => {
    const response = await apiClient.get<DisputeEvidence[]>(
      `/Dispute/${disputeId}/evidence`
    );
    return response.data;
  },

  getTimeline: async (disputeId: string): Promise<DisputeTimeline[]> => {
    const response = await apiClient.get<DisputeTimeline[]>(
      `/Dispute/${disputeId}/timeline`
    );
    return response.data;
  },

  getStats: async (companyOwnerId?: string): Promise<DisputeStats> => {
    const params = companyOwnerId ? `?companyOwnerId=${companyOwnerId}` : '';
    const response = await apiClient.get<DisputeStats>(`/Dispute/stats${params}`);
    return response.data;
  },

  submitEvidence: async (disputeId: string, request: SubmitEvidenceRequest): Promise<void> => {
    await apiClient.post(`/Dispute/${disputeId}/evidence/submit`, request);
  },

  uploadDocument: async (
    disputeId: string,
    file: File,
    evidenceType: string
  ): Promise<DisputeEvidence> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('evidenceType', evidenceType);
    const response = await apiClient.post<DisputeEvidence>(
      `/Dispute/${disputeId}/documents/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  downloadEvidence: async (evidenceId: string): Promise<Blob> => {
    // Use native fetch instead of axios to avoid binary data corruption
    // (axios transformResponse can JSON-stringify binary responses)
    const token = useAuthStore.getState().token;
    const response = await fetch(`/api/Dispute/evidence/${evidenceId}/download`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }

    return response.blob();
  },

  acceptDispute: async (disputeId: string): Promise<void> => {
    await apiClient.post(`/Dispute/${disputeId}/accept`);
  },

  syncFromStripe: async (): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>('/Dispute/sync');
    return response.data;
  },

  recollectEvidence: async (disputeId: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(
      `/Dispute/${disputeId}/evidence/recollect`
    );
    return response.data;
  },

  retryAutoResponse: async (disputeId: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(
      `/Dispute/${disputeId}/auto-response/retry`
    );
    return response.data;
  },
};
